"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { CalendarDays, Check, ChevronLeft, ChevronRight, Droplet, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stepLabels = [{
  num: "01",
  label: "Coverage"
}, {
  num: "02",
  label: "Details"
}, {
  num: "03",
  label: "Timeline"
}, {
  num: "04",
  label: "Contact"
}];
const concerns = [{
  value: "Auto",
  icon: Droplet,
  desc: "Comprehensive vehicle protection"
}, {
  value: "Home",
  icon: Sparkles,
  desc: "Secure your primary residence"
}, {
  value: "Life",
  icon: Droplet,
  desc: "Future-proof your family legacy"
}, {
  value: "Business",
  icon: Sparkles,
  desc: "Enterprise risk management"
}];
const routineSteps = ["Liability", "Collision", "Comprehensive", "Personal Property", "Structural", "Flood", "Term Life", "General Liability"];
const timeSlots = ["Morning (9 AM – 12 PM)", "Afternoon (12 PM – 3 PM)", "Late Afternoon (3 PM – 6 PM)", "Evening (6 PM – 9 PM)"];
const services = ["Personal Insurance Review", "Commercial Risk Assessment", "Legacy Planning Consultation", "Policy Optimization Audit"];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedConcern, setSelectedConcern] = useState<string>("");
  const [selectedRoutine, setSelectedRoutine] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [selectedService, setSelectedService] = useState<string>("");
  const referenceCode = `VANG-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
  const toggleRoutine = (item: string) => {
    setSelectedRoutine(prev => prev.includes(item) ? prev.filter(r => r !== item) : [...prev, item]);
  };
  const goNext = () => setCurrentStep(s => Math.min(s + 1, 3));
  const goBack = () => setCurrentStep(s => Math.max(s - 1, 0));
  if (isSubmitted && !submitError) {
    return <div className="bg-card border border-border rounded-lg p-12 md:p-16 text-center">
        <div className="inline-flex items-center justify-center size-14 rounded-lg bg-accent mb-8">
          <Sparkles className="size-6 text-primary" />
        </div>
        <h3 className="font-serif font-default text-3xl md:text-4xl text-foreground mb-4 tracking-tight">
          Request successfully submitted.
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto mb-8 leading-relaxed">
          A Vanguard Shield advisor will review your details and contact you within 24 hours to finalize your coverage plan.
        </p>
        <div className="inline-block border border-border bg-background px-6 py-3 rounded-lg mb-8">
          <span className="font-mono text-xs text-muted-foreground tracking-widest uppercase mr-3">
            Reference
          </span>
          <span className="font-mono text-sm text-foreground tracking-wider">
            {referenceCode}
          </span>
        </div>
        <div>
          <Button onClick={resetForm} variant="outline" className="rounded-lg">
            Start New Quote
          </Button>
        </div>
      </div>;
  }
  return <div className="bg-card border border-border rounded-lg shadow-md overflow-hidden">
      <div className="px-6 md:px-12 pt-10 md:pt-12 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {stepLabels.map((step, i) => <div data-index={i} key={step.num} className="flex flex-col gap-2">
              <div className={`h-[2px] w-full transition-colors duration-500 ${i < currentStep ? "bg-primary" : i === currentStep ? "bg-primary" : "bg-border"}`} />
              <div className="flex items-baseline gap-2">
                <span className={`font-mono text-xs tracking-widest transition-colors ${i <= currentStep ? "text-primary" : "text-muted-foreground"}`}>
                  {step.num}
                </span>
                <span className={`text-xs md:text-sm transition-colors ${i <= currentStep ? "text-foreground" : "text-muted-foreground"}`}>
                  {step.label}
                </span>
              </div>
            </div>)}
        </div>
      </div>

      <Form onSubmit={handleSubmit} className="px-6 md:px-12 pb-10 md:pb-12">
        {currentStep === 0 && <div className="animate-in fade-in slide-in-from-right-4 duration-500">
            <h3 className="font-serif font-default text-2xl md:text-3xl text-foreground mb-2 tracking-tight">
              Select your coverage.
            </h3>
            <p className="text-muted-foreground text-sm mb-8">
              Choose the primary insurance category for your quote.
            </p>

            <RadioGroup value={selectedConcern} onValueChange={setSelectedConcern} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {concerns.map((concern, i) => {
            const Icon = concern.icon;
            const isActive = selectedConcern === concern.value;
            return <label data-index={i} key={concern.value} htmlFor={`concern-${concern.value}`} className={`relative cursor-pointer border rounded-lg p-5 transition-all duration-300 ${isActive ? "border-primary bg-accent shadow-sm" : "border-border bg-background hover:border-primary/40"}`}>
                    <RadioGroupItem value={concern.value} id={`concern-${concern.value}`} className="sr-only" />
                    <Icon className={`size-5 mb-3 ${isActive ? "text-primary" : "text-muted-foreground"}`} />
                    <div className="font-serif font-default text-lg text-foreground mb-1">
                      {concern.value}
                    </div>
                    <div className="text-xs text-muted-foreground leading-relaxed">
                      {concern.desc}
                    </div>
                  </label>;
          })}
            </RadioGroup>

            <FormField control={control} name="service_interest" rules={{
          required: "Please select a service"
        }} render={({
          field
        }) => <FormItem className="mt-8">
                  <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                    Specific Service
                  </FormLabel>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    {services.map((service, i) => {
              const isActive = selectedService === service;
              return <Button data-index={i} key={service} type="button" onClick={() => {
                setSelectedService(service);
                field.onChange(service);
              }} variant="outline" className={`justify-start rounded-lg text-left h-auto py-3 px-4 ${isActive ? "border-primary bg-accent text-foreground" : "border-border bg-background text-muted-foreground"}`}>
                          {service}
                        </Button>;
            })}
                  </div>
                  <FormMessage />
                </FormItem>} />
          </div>}

        {currentStep === 1 && <div className="animate-in fade-in slide-in-from-right-4 duration-500">
            <h3 className="font-serif font-default text-2xl md:text-3xl text-foreground mb-2 tracking-tight">
              Policy requirements.
            </h3>
            <p className="text-muted-foreground text-sm mb-8">
              Select the coverage features you require.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              {routineSteps.map((step, i) => <label data-index={i} key={step} htmlFor={`routine-${step}`} className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition-colors ${selectedRoutine.includes(step) ? "border-primary bg-accent" : "border-border bg-background hover:border-primary/40"}`}>
                  <Checkbox id={`routine-${step}`} checked={selectedRoutine.includes(step)} onCheckedChange={() => toggleRoutine(step)} />
                  <span className="text-sm text-foreground">{step}</span>
                </label>)}
            </div>

            <div>
              <Label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                Additional details
              </Label>
              <Textarea placeholder="Provide any specific details regarding your assets or risk profile..." className="mt-3 bg-background border-border min-h-28 rounded-lg resize-none" />
            </div>
          </div>}

        {currentStep === 2 && <div className="animate-in fade-in slide-in-from-right-4 duration-500">
            <h3 className="font-serif font-default text-2xl md:text-3xl text-foreground mb-2 tracking-tight">
              Consultation timing.
            </h3>
            <p className="text-muted-foreground text-sm mb-8">
              Select a preferred date and time for your advisor call.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 items-start">
              <FormField control={control} name="preferred_date" rules={{
            required: "Please select a date"
          }} render={({
            field
          }) => <FormItem>
                    <FormControl>
                      <div className="bg-background border border-border rounded-lg p-3 inline-block">
                        <SafeHydrate><Calendar mode="single" selected={selectedDate} onSelect={date => {
                    setSelectedDate(date);
                    field.onChange(date ? date.toISOString().split("T")[0] : "");
                  }} disabled={date => date < new Date()} /></SafeHydrate>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="preferred_time" render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                      Available Time Slots
                    </FormLabel>
                    <div className="flex flex-col gap-3">
                      {timeSlots.map((slot, i) => {
                const isActive = selectedTime === slot;
                return <Button data-index={i} key={slot} type="button" onClick={() => {
                  setSelectedTime(slot);
                  field.onChange(slot);
                }} variant="outline" className={`justify-between rounded-lg h-auto py-3 px-5 ${isActive ? "border-primary bg-accent text-foreground" : "border-border bg-background text-muted-foreground"}`}>
                            <span>{slot}</span>
                            {isActive && <Check className="size-4 text-primary" />}
                          </Button>;
              })}
                    </div>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>}

        {currentStep === 3 && <div className="animate-in fade-in slide-in-from-right-4 duration-500">
            <h3 className="font-serif font-default text-2xl md:text-3xl text-foreground mb-2 tracking-tight">
              Final contact details.
            </h3>
            <p className="text-muted-foreground text-sm mb-8">
              Provide your information to receive your quote.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <FormField control={control} name="full_name" rules={{
            required: "Full name is required",
            minLength: {
              value: 2,
              message: "Min 2 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                      Full Name
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" className="border-0 border-b border-border bg-transparent px-0 focus-visible:border-primary focus-visible:ring-0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="email_address" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                      Email Address
                    </FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john@example.com" className="border-0 border-b border-border bg-transparent px-0 focus-visible:border-primary focus-visible:ring-0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="phone_number" rules={{
            required: "Phone number is required"
          }} render={({
            field
          }) => <FormItem className="md:col-span-2">
                    <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                      Phone Number
                    </FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="+1 (555) 123-4567" className="border-0 border-b border-border bg-transparent px-0 focus-visible:border-primary focus-visible:ring-0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <FormField control={control} name="message" render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                    Additional Notes
                  </FormLabel>
                  <FormControl>
                    <Textarea placeholder="Any specific concerns or preferences you'd like to share..." className="bg-background border-border min-h-28 rounded-lg resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>}

        {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
            <p className="text-destructive text-sm">{submitError}</p>
          </div>}

        <div className="mt-10 pt-8 border-t border-border flex items-center justify-between gap-4">
          {currentStep > 0 ? <Button type="button" variant="ghost" onClick={goBack} className="text-muted-foreground hover:text-foreground rounded-lg">
              <ChevronLeft className="size-4" />
              Back
            </Button> : <span className="font-mono text-xs text-muted-foreground tracking-widest uppercase">
              Step {currentStep + 1} of 4
            </span>}

          {currentStep < 3 ? <Button type="button" onClick={goNext} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6">
              Continue
              <ChevronRight className="size-4" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6">
              {isSubmitting ? "Submitting..." : "Submit Request"}
              <Sparkles className="size-4" />
            </Button>}
        </div>
      </Form>
    </div>;
}
export default function InstantQuoteEngine() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="3168" id="instant-quote-engine" className="bg-background py-24 md:py-36 px-4 md:px-8">
      <div ref={ref} className={`max-w-6xl mx-auto transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-14 md:mb-20 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="h-px w-8 bg-primary/40"></span>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
              Vanguard Shield Insurance
            </span>
            <span className="h-px w-8 bg-primary/40"></span>
          </div>
          <h2 className="font-serif font-default text-5xl md:text-6xl lg:text-7xl text-foreground leading-[1.05] tracking-tight">
            Secure your
            <br />
            <span className="italic text-primary">peace of mind.</span>
          </h2>
          <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed max-w-xl mx-auto">
            Professional coverage solutions designed to protect your assets, your family, and your future with absolute certainty.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 text-xs font-mono text-muted-foreground tracking-widest uppercase">
            <CalendarDays className="size-3.5" />
            <span>24/7 Claims Support · Nationwide Coverage</span>
          </div>
        </div>

        <div className="max-w-[960px] mx-auto">
          <FormProvider formKey="instant_quote_form_home" formId="instant_quote_form_home" sectionName="instant-quote-engine" defaultValues={{
          full_name: "",
          email_address: "",
          phone_number: "",
          preferred_date: "",
          preferred_time: "",
          service_interest: "",
          message: ""
        }} validationRules={{
          full_name: {
            required: true,
            minLength: 2
          },
          email_address: {
            required: true,
            pattern: "email"
          },
          phone_number: {
            required: true,
            pattern: "phone"
          },
          preferred_date: {
            required: true
          },
          service_interest: {
            required: true
          }
        }} submitText="Submit Request" successMessage="Your request has been received. We'll be in touch shortly." errorMessage="We couldn't submit your request. Please try again.">
            <InquiryForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}